
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import React from "react";

import { Heading } from "@/components/Heading";
import { Head } from "@/components/Head";
import { Grid } from "@/components/Grid";
import { BlogText } from "@/components/BlogText";
import { CTASection } from "@/sections/CTASection";
import { NewsletterSignupSection } from "@/sections/NewsletterSignupSection";
import { useTheme } from "@/hooks/useTheme";

const XDComponentsToFigma = () => {
  const { colors } = useTheme();
  return (
    <>
      <Head
        pageNamespace="page/xd-components-to-figma"
        ogImage="/images/xd2figma-og-image.jpg"
      />
      <div>
        <Grid
          customStyle={{
            maxWidth: 800,
            flexDirection: "column",
            alignItems: "flex-start",
            paddingBottom: "0 !important",
          }}
        >
          <Heading as="h1">How to convert Components from XD to Figma</Heading>

          <BlogText customStyle={{ marginTop: 29 }}>
            Converting complex design files between{" "}
            <a
              href="https://magicul.io/converter/xd-to-figma"
              rel="noreferrer"
              target="_blank"
              style={{
                color: colors.RED,
                fontWeight: "bold",
              }}
            >
              XD and Figma
            </a>{" "}
            can take days to accomplish.
          </BlogText>
          <BlogText>
            If the file includes reusable Component Libraries and a Design
            System, then those days will quickly stretch into weeks or even
            months.
          </BlogText>
          <BlogText>
            Our Adobe XD to Figma converter saves you time and converts even:
          </BlogText>
          <ul>
            <li>
              <BlogText>Components</BlogText>
            </li>
            <li>
              <BlogText>Component Instances</BlogText>
            </li>
            <li>
              <BlogText>Component Variants</BlogText>
            </li>
            <li>
              <BlogText>Component Layout Properties</BlogText>
            </li>
          </ul>

          <BlogText>
            Magicul automates the soulless task of rebuilding your design in
            incompatible tools and simplifies conversions down to only a few
            clicks.
          </BlogText>
          <section>
            <strong>
              <BlogText>Table of Contents</BlogText>
            </strong>
            <ul>
              <li>
                <BlogText>
                  A closer look at the XD to Figma conversion ✨
                </BlogText>
              </li>
              <li>
                <BlogText>
                  5 Steps to convert your files from XD to Figma 🐾
                </BlogText>
              </li>
              <li>
                <BlogText>
                  Two features of Adobe XD we can't convert to Figma 🙅‍♀️
                </BlogText>
              </li>
              <li>
                <BlogText>1 free artboard conversion trial 👍</BlogText>
              </li>
            </ul>
          </section>

          <Heading as="h3">
            A closer look at the XD to Figma Conversion ✨
          </Heading>
          <BlogText>
            Here you see the before and after gifs of a file converted from XD
            to Figma. The file converted with all Components 🎉 and all of it's
            instances.
          </BlogText>

          <img
            style={{
              paddingTop: 29,
              width: "110%",
              marginBottom: 29,
            }}
            src="/images/tutorials/xd-components-to-figma/1-xd-unconverted.gif"
            alt="gif xd design demo before conversion to Figma"
          />

          <BlogText>
            Now let's take a closer look at the Components converted to Figma
          </BlogText>
          <img
            style={{
              margin: "auto",
              paddingTop: 29,
              width: "110%",

              marginBottom: 29,
            }}
            src="/images/tutorials/xd-components-to-figma/3-converted-file-in-figma.gif"
            alt="converted file in figma"
          />

          <div
            style={{
              borderLeft: "4px solid #51B4DB",
              backgroundColor: "#F3F5F7",
              marginBottom: "2rem",
            }}
          >
            <BlogText
              customStyle={{
                marginLeft: 10,
                marginTop: 10,
              }}
            >
              <strong>
                Struggling to find Master Components in Figma after the
                conversion?
              </strong>
            </BlogText>
            <BlogText
              customStyle={{
                marginLeft: 10,
                marginRight: 10,
              }}
            >
              The master Components from XD were moved to the Symbols page
              during the conversion.
            </BlogText>
            <BlogText
              customStyle={{
                marginLeft: 10,
              }}
            >
              🔥 Hot Tip: right click - Main Component - Go to Main Component.
            </BlogText>
          </div>
          <section>
            <img
              style={{
                width: "100%",
                marginBottom: 29,
              }}
              src="/images/tutorials/xd-components-to-figma/4-go-to-main-component.png"
              alt="go to the main component in Figma button"
            />
          </section>

          <BlogText>
            <strong>Did you notice this?</strong>
          </BlogText>

          <img
            style={{
              marginBottom: 29,
              width: "80%",
            }}
            src="/images/tutorials/xd-components-to-figma/5-clip-content.gif"
            alt="conversion issues clip content in Figma"
          />

          <BlogText>
            The conversion is fine. What we're seeing here is a difference in
            how Figma and XD treat certain properties.
          </BlogText>
          <BlogText>
            In this case, the clip content property is true by default in XD.
          </BlogText>

          <BlogText>
            If you run into any issues without an easy fix, feel free to message
            us in the support chat for prompt and 24/7 assistance. Or you can
            consult the{" "}
            <a
              href="https://help.magicul.io/en/"
              target="_blank"
              rel="noreferrer"
              style={{
                color: colors.RED,
                fontWeight: "bold",
              }}
            >
              help articles to troubleshoot for easy fixes like you see above.
            </a>
          </BlogText>

          <Heading as="h3">Layouts and Resizing</Heading>

          <BlogText>
            Magicul supports frame and Component layout and resizing policies.
            This means that Component layout rules and tethers that we set up in
            XD will persist in Figma.
          </BlogText>

          <img
            style={{
              width: "100%",
              marginBottom: 29,
            }}
            src="/images/tutorials/xd-components-to-figma/6-demonstrating-layout.gif"
            alt="demonstrating layouts in Figma components"
          />
          <BlogText>
            Components must retain a high degree of editability so future
            designers can continue to iterate or take the design in a completely
            different direction.
          </BlogText>

          <Heading as="h3">
            Parent/Child or Master/Instance Relationships
          </Heading>
          <BlogText>Magicul understands complex Component hierarchy.</BlogText>
          <BlogText>
            When your Design System is as much a feat of engineering as it is a
            collection of UI elements, you can’t leave all that effort and
            business value behind in different tooling.
          </BlogText>
          <BlogText>You can't copy it across as an SVG either.</BlogText>
          <BlogText>
            Magicul is the only converter capable of moving Components between
            XD and Figma, and by process of elimination, Magicul is also the
            only converter that can understand Component hierarchy- Components
            inside of Components - and knows how to move these relationships
            between tools.
          </BlogText>

          <img
            style={{
              marginBottom: 29,

              width: "100%",
            }}
            src="/images/tutorials/xd-components-to-figma/7-child-components.gif"
            alt="demonstrating child components in Figma"
          />

          <Heading as="h3">
            5 steps to convert your files from XD to Figma 🐾
          </Heading>
          <BlogText>
            <strong> Step 1:</strong> Save the XD as a local document
          </BlogText>
          <img
            style={{
              width: "60%",
              marginBottom: 29,
            }}
            src="/images/tutorials/xd-components-to-figma/8-save-a-local-document.png"
            alt="saving as a local document"
          />
          <BlogText>
            <strong> Step 2:</strong> Go to the Magicul homepage and select the{" "}
            <a
              style={{
                color: colors.RED,
                fontWeight: "bold",
              }}
              href="https://magicul.io/converter/xd-to-figma"
              target="_blank"
              rel="noreferrer"
            >
              XD to Figma conversion
            </a>{" "}
            option.
          </BlogText>
          <img
            style={{
              width: "60%",
              marginBottom: 29,
            }}
            src="/images/tutorials/xd-components-to-figma/9-upload-your-file.png"
            alt="upload your file"
          />
          <BlogText>
            <strong>Step 3:</strong> Upload your XD file and select convert now
          </BlogText>
          <img
            style={{
              width: "60%",
              marginBottom: 29,
            }}
            src="/images/tutorials/xd-components-to-figma/10-preview.JPG"
            alt="preview file magicul adobe xd to figma conversion"
          />
          <BlogText>
            <strong>Step 4:</strong> Pay for your Magicul conversion.
          </BlogText>
          <BlogText>
            For more information about pricing{" "}
            <a
              style={{
                color: colors.RED,
                fontWeight: "bold",
              }}
              href="https://magicul.io/converter/xd-to-figma"
              target="_blank"
              rel="noreferrer"
            >
              click here
            </a>
            .
          </BlogText>
          <BlogText>
            <strong>Step 5:</strong> Download the converted file and open it in
            Figma
          </BlogText>
          <img
            style={{
              width: "60%",
              marginBottom: 29,
            }}
            src="/images/tutorials/xd-components-to-figma/11-open-conversion-in-figma.png"
            alt="open conversion in Figma after adobe XD conversion"
          />

          <Heading as="h3">
            Two features of Adobe XD we can't convert to Figma
          </Heading>
          <BlogText>
            <strong>1: Auto Aware Layouts to Auto Layouts</strong>
          </BlogText>
          <BlogText>
            Figma’s auto layouts are different from XD’s auto aware layout, and
            we don't support a conversion between them <strong>(yet)</strong>.
          </BlogText>
          <div
            style={{
              borderLeft: "4px solid #51B4DB",
              backgroundColor: "#F3F5F7",
              marginBottom: "2rem",
            }}
          >
            <BlogText
              customStyle={{
                marginLeft: 10,
                marginTop: 10,
              }}
            >
              Groups and Components help quickly set up Auto Layouts
            </BlogText>
            <BlogText
              customStyle={{
                marginLeft: 10,
                marginTop: 10,
              }}
            >
              Magicul supports Frames, Component, and Group Layouts.
              Preemptively grouping elements in XD can save time by letting you
              or your team apply auto layouts to Figma groups, frames,
              Components, and their instances, after the conversion.
            </BlogText>
            <BlogText
              customStyle={{
                marginLeft: 10,
                marginTop: 10,
              }}
            >
              If you think auto layouts to auto aware layout is an important
              feature, put some pressure on us to release it faster:
            </BlogText>
            <BlogText
              customStyle={{
                marginLeft: 10,
                marginTop: 10,
              }}
            >
              <a
                style={{
                  color: colors.RED,
                  fontWeight: "bold",
                }}
                href="mailto:support@magicul.io"
              >
                support@magicul.io
              </a>
            </BlogText>
          </div>

          <BlogText>
            <strong>2: Transitions and Prototyping</strong>
          </BlogText>
          <BlogText>
            Magicul uses Figma’s API to build exact copies of your XD files in
            Figma. There are limitations to what Figma will let people do with
            its API. Because of these limitations, we don't support Protoyping
            and Transitions from XD to Figma as of right now.
          </BlogText>

          <div
            style={{
              borderLeft: "4px solid #51B4DB",
              backgroundColor: "#F3F5F7",
              marginBottom: "2rem",
            }}
          >
            <BlogText
              customStyle={{
                marginLeft: 10,
                marginTop: 10,
              }}
            >
              Figma's Prototyping is read-only
            </BlogText>
            <BlogText customStyle={{ marginLeft: 10, marginTop: 10 }}>
              We are developing a work-around. If Prototyping and Transition
              connections are all that are keeping you in XD, please let us
              know, and we'll hurry Prototyping support along.
            </BlogText>
            <BlogText
              customStyle={{
                marginLeft: 10,
                marginTop: 10,
              }}
            >
              <a
                style={{
                  color: colors.RED,
                  fontWeight: "bold",
                }}
                href="mailto:support@magicul.io"
              >
                support@magicul.io
              </a>
            </BlogText>
          </div>

          <Heading as="h3">The next step - 1 Free Artboard Trial 👍</Heading>
          <BlogText>What next?</BlogText>
          <BlogText>You need to try it out for yourself.</BlogText>
          <BlogText>
            Magicul offers a free trial so you can convert your own file and
            stress test the conversion.
          </BlogText>
          <BlogText>
            Contact us in the support if you’re interested in converting a
            single artboard file.
          </BlogText>
        </Grid>
        <CTASection
          title="Guides like this will only get you so far. Feel the quality with your own hands. Convert your own file now!"
          href="/"
        />
        <NewsletterSignupSection />
      </div>
    </>
  );
};

export default XDComponentsToFigma;


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        
// @ts-ignore
        
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          
// @ts-ignore
          props: {
// @ts-ignore
            
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/tutorials/xd-components-to-figma',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  